import React, { useState, useEffect, useRef } from "react";
import { ScreenType } from "./types";
import Screen from "./screen";
import logo from "./logo.png";
import "./monitor.css";

const Monitor: React.FC = () => {
    const [displayedScreens, setDisplayedScreens] = useState<ScreenType[]>([]);
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [progress, setProgress] = useState<number>(0);
    const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
    const currentIndexRef = useRef<number>(currentIndex);
    const rotationStartRef = useRef<number | null>(null);
    const progressTimerRef = useRef<number | null>(null);

    const ROTATION_INTERVAL = 20000; // 20 seconds
    // Transition timings matching monitor.css
    const FADE_DELAY = 20; // ms after logo begins
    const FADE_DURATION = 300; // ms
    const TRANSITION_TOTAL = FADE_DELAY + FADE_DURATION; // buffer

    // Generate snowflakes
    const snowflakes = Array.from({ length: 0 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        animationDuration: 10 + Math.random() * 20,
        animationDelay: Math.random() * 10,
        size: 2 + Math.random() * 4,
        opacity: 0.3 + Math.random() * 0.7
    }));

    // Load displayed screens from localStorage
    useEffect(() => {
        const loadScreens = () => {
            const displayedScreensKey = 'displayedScreens';
            const currentDisplayed = localStorage.getItem(displayedScreensKey);
            const raw: any[] = currentDisplayed ? JSON.parse(currentDisplayed) : [];
            // Normalize saved entries: accept either ScreenType ({data,...}) or raw data objects
            const screens: ScreenType[] = (raw || []).map((entry: any) => {
                if (entry && typeof entry === 'object' && entry.data !== undefined) {
                    return entry as ScreenType;
                }
                // wrap raw data object
                return { data: entry, visible: true } as ScreenType;
            }).filter((s: any) => s && s.data !== undefined);
            console.debug('[Monitor] loadScreens -> raw=', raw, 'normalized=', screens);
            setDisplayedScreens(screens);
            // Reset index if out of bounds
            if (screens.length === 0) {
                setCurrentIndex(0);
            } else if (currentIndex >= screens.length) {
                setCurrentIndex(0);
            }
        };

        loadScreens();

        // Optional: Listen for storage changes to update in real-time
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'displayedScreens') {
                loadScreens();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Rotate through screens using a single timer scheduler.
    useEffect(() => {
        let timer: number | null = null;
        let endTimeout: number | null = null;

        const findNextValid = (start: number) => {
            const len = displayedScreens.length;
            if (len === 0) return -1;
            for (let i = 1; i <= len; i++) {
                const idx = (start + i) % len;
                if (displayedScreens[idx] && displayedScreens[idx].data) return idx;
            }
            return -1;
        };

        const scheduleNext = () => {
            // clear any existing timer
            if (timer !== null) {
                clearTimeout(timer);
                timer = null;
            }

            // do nothing if not enough screens
            if (displayedScreens.length <= 1) {
                setProgress(0);
                setIsTransitioning(false);
                return;
            }

            // record start time for progress indicator and start progress updater
            rotationStartRef.current = Date.now();
            setProgress(0);
            if (progressTimerRef.current !== null) {
                clearInterval(progressTimerRef.current);
                progressTimerRef.current = null;
            }
            progressTimerRef.current = window.setInterval(() => {
                if (!rotationStartRef.current) return;
                const elapsed = Date.now() - rotationStartRef.current;
                const pct = Math.min((elapsed / ROTATION_INTERVAL) * 100, 100);
                setProgress(pct);
            }, 100);

            // schedule the next rotation
            timer = window.setTimeout(() => {
                // progress reached ROTATION_INTERVAL; make sure progress is full and stop updater
                if (progressTimerRef.current !== null) {
                    clearInterval(progressTimerRef.current);
                    progressTimerRef.current = null;
                }
                setProgress(100);
                // start transition
                setIsTransitioning(true);
                // find candidate
                const candidate = findNextValid(currentIndexRef.current);
                if (candidate === -1) {
                    console.warn('[Monitor] no valid next screen found (scheduler)');
                    setIsTransitioning(false);
                    // restart scheduling
                    scheduleNext();
                    return;
                }

                // apply swap immediately so the new screen is visible under the overlay
                setCurrentIndex(candidate);

                // end transition after TRANSITION_TOTAL
                endTimeout = window.setTimeout(() => {
                    setIsTransitioning(false);
                    setProgress(0);
                    rotationStartRef.current = null;
                    // reschedule the next rotation
                    scheduleNext();
                }, TRANSITION_TOTAL);
            }, ROTATION_INTERVAL);
        };

        // kick off scheduler
        scheduleNext();

        return () => {
            if (timer !== null) clearTimeout(timer);
            if (endTimeout !== null) clearTimeout(endTimeout);
            if (progressTimerRef.current !== null) {
                clearInterval(progressTimerRef.current);
                progressTimerRef.current = null;
            }
            rotationStartRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayedScreens.length]);

    // keep ref updated so timers/closures can read latest index without stale closures
    useEffect(() => {
        currentIndexRef.current = currentIndex;
    }, [currentIndex]);

    // derive current screen and timing info
    const currentScreen = displayedScreens[currentIndex] ?? null;
    // Prefer using rotationStartRef to compute remaining time so indicator matches scheduled timeout.
    let remainingMs: number;
    if (rotationStartRef.current) {
        const elapsed = Date.now() - rotationStartRef.current;
        remainingMs = Math.max(0, ROTATION_INTERVAL - elapsed);
    } else {
        // If not scheduled (single screen or not yet scheduled), show full interval
        remainingMs = ROTATION_INTERVAL;
    }
    const secondsRemaining = Math.max(0, Math.ceil(remainingMs / 1000));

    // If no screens to display, show the project's logo centered (clean, professional fallback)
    if (displayedScreens.length === 0) {
        return (
            <div className="fixed inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900">
                <img src={logo} alt="logo" className="h-120 w-auto opacity-95" />
                <div
                    className="mt-0 text-white text-5xl sm:text-5xl font-semibold tracking-wide drop-shadow-lg text-center"
                    style={{ textShadow: '0 4px 22px rgba(0,0,0,0.6)' }}
                >
                    Compétition de Noël
                </div>
            </div>
        );
    }

    console.debug('[Monitor] render currentIndex=', currentIndex, 'currentScreen=', currentScreen);

    return (
        <div className="fixed inset-0 overflow-auto tv-wrapper">
            {/* Snowflakes Background */}
            <div className="fixed inset-0 pointer-events-none z-30 overflow-hidden">
                {snowflakes.map((flake) => (
                    <div
                        key={flake.id}
                        className="snowflake"
                        style={{
                            left: `${flake.left}%`,
                            width: `${flake.size}px`,
                            height: `${flake.size}px`,
                            animationDuration: `${flake.animationDuration}s`,
                            animationDelay: `${flake.animationDelay}s`,
                            opacity: flake.opacity
                        }}
                    />
                ))}
            </div>

            {/* Progress bar at the top */}
            {displayedScreens.length > 1 && (
                <div className="fixed top-0 left-0 right-0 h-2 bg-gray-800 z-20 progress-top">
                    <div
                        className="h-full bg-gradient-to-r from-blue-950 via-blue-900 to-blue-300 transition-all duration-100 ease-linear"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            )}

            <div className={`tv-screen ${isTransitioning ? 'transitioning' : ''}`}>
                {currentScreen && currentScreen.data ? (
                    <Screen key={currentIndex} screen={currentScreen} />
                ) : (
                    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white">
                        <div className="text-center">
                            <img src={logo} alt="logo" className="h-40 w-auto opacity-90 mx-auto mb-4" />
                            <div className="text-lg text-gray-300">No screen data available</div>
                        </div>
                    </div>
                )}
            </div>

            {/* TV overlay: logo + scanlines that animate during transition */}
            <div className={`tv-overlay ${isTransitioning ? 'animate' : ''}`}>
                <div className={`tv-bars-top ${isTransitioning ? 'animate' : ''}`} />
                <div className={`tv-bars-bottom ${isTransitioning ? 'animate' : ''}`} />
                <div className="inner">
                    <img src={logo} className="tv-logo" alt="logo" />
                </div>
                <div className={`tv-flash ${isTransitioning ? 'animate' : ''}`} />
                <div className={`tv-grain ${isTransitioning ? 'animate' : ''}`} />
                <div className="tv-scanlines" />
            </div>

            <div className="tv-frame" />

            {/* Screen indicator with countdown */}
            {displayedScreens.length > 1 && (
                <div className="fixed bottom-4 right-4 bg-black bg-opacity-70 text-white px-4 py-3 rounded-lg z-10 shadow-lg">
                    <div className="text-sm font-semibold">
                        Screen {currentIndex + 1} of {displayedScreens.length}
                    </div>
                    <div className="text-xs text-gray-300 mt-1">
                        Next in {secondsRemaining}s
                    </div>
                </div>
            )}
        </div>
    );
};

export default Monitor;
